import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowDown, Check, ShieldCheck } from "lucide-react";
import { Breadcrumbs } from "../components/sections/Breadcrumbs";
import { IsdeCheck } from "../components/isde/IsdeCheck";
import { IsdeSeoSections } from "../components/isde/IsdeSeoSections";
import { ProductFAQ } from "../components/product/ProductFAQ";
import { ProductEndCTA } from "../components/product/RelatedProducts";
import { ISDE_TOC, buildIsdeFaq } from "../data/isdeContent";
import { usePageSeo } from "../lib/siteTexts";
import { ISDE_PATH, eurRate, nextAnnounced, nlDate, rateOf, ruleFor, useIsdeRules } from "../lib/isde";
import { buildBreadcrumbsJsonLd, buildFaqJsonLd, buildServiceJsonLd, useSEO } from "../lib/seo";
import { useFaqs } from "../lib/faqs";

const initialFromQuery = (search) => {
  const p = new URLSearchParams(search);
  const glass = ["hr_plus_plus", "hr_triple"].includes(p.get("glas")) ? p.get("glas") : null;
  const m2 = /^\d{1,3}$/.test(p.get("m2") || "") ? p.get("m2") : "";
  return { ...(glass ? { glass } : {}), ...(m2 ? { area_mode: "m2", glass_m2: m2 } : {}) };
};

const Summary = ({ cur, nxt }) => (
  <aside className="card p-6" data-testid="isde-summary">
    <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gold">In het kort{cur ? ` · ${cur.year}` : ""}</p>
    <dl className="mt-4 space-y-3 text-[15px]">
      {[["HR++ glas", rateOf(cur, "hr_plus_plus")], ["HR+++ triple glas in nieuwe kozijnen", rateOf(cur, "triple_new_frames")]].map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-3 border-b border-slate-100 pb-3"><dt className="text-slate-600">{k}</dt><dd className="font-display text-[20px] font-bold text-ink">{v != null ? `${eurRate(v)}/m²` : "—"}</dd></div>
      ))}
      {cur && <div className="flex justify-between gap-3"><dt className="text-slate-600">Oppervlak</dt><dd className="font-semibold text-ink">{cur.min_m2} – {cur.max_m2} m²</dd></div>}
    </dl>
    {nxt && (
      <p className="mt-4 rounded-lg bg-gold-soft px-3 py-2 text-[13px] text-ink" data-testid="isde-summary-announced">
        Aangekondigd voor {nxt.year} (nog niet definitief): {[["HR++", rateOf(nxt, "hr_plus_plus")], ["HR+++", rateOf(nxt, "triple_new_frames")]].filter(([, v]) => v != null).map(([k, v]) => `${k} ${eurRate(v)}/m²`).join(", ")}.
      </p>
    )}
    {cur?.last_checked && <p className="mt-4 flex items-center gap-1.5 text-[12.5px] text-slate-500"><ShieldCheck className="h-4 w-4 text-maas" aria-hidden="true" /> Gecontroleerd bij RVO op {nlDate(cur.last_checked)}</p>}
  </aside>
);

export default function IsdeSubsidieKozijnen() {
  const { search } = useLocation();
  const [initial] = useState(() => initialFromQuery(search));
  const rules = useIsdeRules();
  const cur = ruleFor(rules, "actief");
  const nxt = nextAnnounced(rules);
  const central = useFaqs(ISDE_PATH) || [];
  const live = buildIsdeFaq(cur, nxt);
  const faq = [...central, ...live];
  // Gestructureerde gegevens alleen voor vragen die uitsluitend hier staan (geen dubbele markering met /veelgestelde-vragen).
  const ownFaq = [...central.filter((f) => !f.on_overview && f.pages?.length === 1), ...live];
  const seo = usePageSeo("isde");
  useSEO({
    title: seo.title, description: seo.description, path: ISDE_PATH,
    jsonLd: buildServiceJsonLd("ISDE-subsidiecheck voor nieuwe kozijnen", "Vrijblijvende indicatie van de ISDE-subsidie voor nieuwe kunststof en aluminium kozijnen met HR++ of HR+++ glas, op basis van de RVO-regels."),
    faqJsonLd: ownFaq.length ? buildFaqJsonLd(ownFaq.map((f) => ({ q: f.question, a: f.answer.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") }))) : null,
    breadcrumbs: buildBreadcrumbsJsonLd([{ label: "Home", href: "/" }, { label: "ISDE-subsidie kozijnen", href: ISDE_PATH }]),
  });
  return (
    <div data-testid="isde-page">
      <section className="bg-slate-50">
        <div className="container-tight pt-4"><Breadcrumbs items={[{ label: "ISDE-subsidie kozijnen", href: ISDE_PATH }]} /></div>
        <div className="container-tight grid gap-10 pb-12 pt-6 lg:grid-cols-12 lg:gap-12">
          <div className="reveal lg:col-span-8">
            <p className="eyebrow">ISDE-subsidie · vrijblijvende subsidiecheck</p>
            <h1 className="mt-3 font-display text-[clamp(1.8rem,2.4vw+1rem,2.75rem)] font-bold leading-tight text-ink text-balance" data-testid="isde-title">ISDE-subsidie bij nieuwe kozijnen: ontdek uw mogelijkheden</h1>
            <p className="lead mt-4 max-w-2xl">Vervangt u bestaande kozijnen door nieuwe kozijnen met HR++ of HR+++ glas? Dan kunt u onder voorwaarden ISDE-subsidie ontvangen. Bereken in vijf korte stappen wat er voor uw woning mogelijk is, zonder contactgegevens achter te laten.</p>
            <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[14px] font-medium text-ink">
              {["Op basis van de actuele RVO-regels", "Geen contactgegevens nodig", "Direct een persoonlijke indicatie"].map((t) => <li key={t} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-maas" aria-hidden="true" /> {t}</li>)}
            </ul>
            <a href="#voorwaarden" className="btn-ghost -ml-3 mt-4" data-testid="isde-conditions-link">Bekijk de ISDE-voorwaarden <ArrowDown className="h-4 w-4" aria-hidden="true" /></a>
            <div id="subsidiecheck" className="mt-6 scroll-mt-24"><IsdeCheck initial={initial} /></div>
          </div>
          <div className="space-y-5 lg:col-span-4 lg:pt-28"><Summary cur={cur} nxt={nxt} /></div>
        </div>
      </section>
      <section className="section">
        <div className="container-tight grid gap-10 lg:grid-cols-12 lg:gap-14">
          <nav aria-label="Op deze pagina" className="hidden lg:col-span-3 lg:block">
            <div className="sticky top-28">
              <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-500">Op deze pagina</p>
              <ul className="mt-3 space-y-1.5 text-[14px]">{ISDE_TOC.map(([id, l]) => <li key={id}><a href={`#${id}`} className="text-slate-600 hover:text-maas">{l}</a></li>)}</ul>
            </div>
          </nav>
          <div className="max-w-3xl lg:col-span-9"><IsdeSeoSections /></div>
        </div>
      </section>
      <ProductFAQ faqs={faq} title="Vragen over ISDE-subsidie bij nieuwe kozijnen" />
      <ProductEndCTA title="Nieuwe kozijnen met HR++ of HR+++ glas? Vraag een prijsindicatie aan." onderwerp="kunststof-kozijnen" preselected={false} />
    </div>
  );
}
